
import { useState, useEffect } from 'react';
import { InsightCard } from '@/types/insight';
import { supabase } from '@/integrations/supabase/client';
import { transformDatabaseContent } from '@/types/civic-education';

export const useInsightRelatedContent = (selectedCard: InsightCard | null) => {
  const [relatedContent, setRelatedContent] = useState({
    podcasts: [],
    videos: [],
    summaries: []
  });
  const [hasRelatedContent, setHasRelatedContent] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!selectedCard) {
      setRelatedContent({ podcasts: [], videos: [], summaries: [] });
      setHasRelatedContent(false);
      return;
    }

    const fetchRelatedContent = async () => {
      setIsLoading(true);
      try {
        // Get content linked to this card
        const { data: linkedContent, error: linkError } = await supabase
          .from('civic_content_card_links')
          .select('content_id')
          .eq('card_id', selectedCard.id);

        if (linkError) {
          console.error("Error fetching linked content:", linkError);
          return;
        }

        // If no linked content is found
        if (!linkedContent || linkedContent.length === 0) {
          setRelatedContent({ podcasts: [], videos: [], summaries: [] });
          setHasRelatedContent(false);
          return;
        }

        // Get the actual content items
        const contentIds = linkedContent.map(link => link.content_id);
        const { data: contentItems, error: contentError } = await supabase
          .from('civic_education_content')
          .select('*')
          .in('id', contentIds)
          .eq('status', 'published');

        if (contentError) {
          console.error("Error fetching content items:", contentError);
          return;
        }

        if (!contentItems || contentItems.length === 0) {
          setRelatedContent({ podcasts: [], videos: [], summaries: [] });
          setHasRelatedContent(false);
          return;
        }

        // Transform and categorize content
        const transformedContent = contentItems.map(item => transformDatabaseContent(item));
        
        const podcasts = transformedContent.filter(item => item.type === 'podcast').map(podcast => ({
          title: podcast.title,
          description: podcast.description,
          episode_id: (podcast as any).episode_id,
          external_url: `https://open.spotify.com/episode/${(podcast as any).episode_id}`,
          internal_url: `https://open.spotify.com/episode/${(podcast as any).episode_id}`
        }));
        
        const videos = transformedContent.filter(item => item.type === 'video').map(video => ({
          title: video.title,
          description: video.description,
          video_id: (video as any).video_id,
          external_url: `https://www.youtube.com/watch?v=${(video as any).video_id}`,
          internal_url: `https://www.youtube.com/watch?v=${(video as any).video_id}`
        }));
        
        const summaries = transformedContent.filter(item => item.type === 'summary').map(summary => ({
          title: summary.title,
          description: summary.description,
          source: (summary as any).source || "Official Document",
          external_url: (summary as any).document_url || null,
          internal_url: null
        }));

        setRelatedContent({ podcasts, videos, summaries });
        setHasRelatedContent(podcasts.length > 0 || videos.length > 0 || summaries.length > 0);
      } catch (error) {
        console.error("Error in useInsightRelatedContent:", error);
        // Fallback to empty content
        setRelatedContent({ podcasts: [], videos: [], summaries: [] });
        setHasRelatedContent(false);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRelatedContent();
  }, [selectedCard]);

  return { relatedContent, hasRelatedContent, isLoading };
};
